import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { IModel } from '../../base/annotation';
import { Metamodel } from 'app/model/base/model';
import { LinkedServiceAdapter } from 'app/model/adapters/linkedservices/linkedservice.adapter';
import { DynamicsAXLinkedService } from 'app/model/linkedservice/dynamics-ax.linkedservice';
import { parameterizablePasswordOrAKVtoSecretBase, parameterizableSecretBaseToModel } from 'app/model/adapters/linkedservices/azure-key-vault.util';
import { GlobalVariables } from 'app/services/globals';
import { dynamicsAxAuthTypes, Credential } from 'shell-models-lib';
import { ReferenceAdapter } from '../reference.adapter';
import { ValueAdapter } from '../value.adapter';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(DynamicsAXLinkedService),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            type: '*',
            connectVia: '*',
            typeProperties: {
                url: '*',
                tenant: '*',
                servicePrincipalId: '*',
                servicePrincipalKey: 'secret',
                aadResourceId: '*',
                encryptedCredential: '*',
                credential: GlobalVariables.enableConnectorUserAuthCred ? '*' : undefined
            },
            parameters: '*'
        }
    }
})
export class DynamicsAXLinkedServiceAdapter extends LinkedServiceAdapter<DynamicsAXLinkedService> {
    protected supportFirstClassParameterExperience = true;

    protected modelConstructor(): new () => DynamicsAXLinkedService {
        return DynamicsAXLinkedService;
    }

    protected model(): IModel {
        return DynamicsAXLinkedService.MODEL;
    }

    protected async propertiesToResourceAsync(linkedService: DynamicsAXLinkedService, linkedServiceResource: Model.LinkedServiceResource): Promise<Model.LinkedServiceResource> {
        const properties = this.properties(linkedServiceResource) as Model.DynamicsAXLinkedService;
        properties.type = Metamodel.modelFor(DynamicsAXLinkedService).name;

        let servicePrincipalId;
        let servicePrincipalKey;
        let credential;
        if (GlobalVariables.enableConnectorUserAuthCred) {
            if (linkedService.authenticationType === dynamicsAxAuthTypes.ServicePrincipal) {
                servicePrincipalId = this.valueToResource(linkedService, DynamicsAXLinkedService.SERVICE_PRINCIPLE_ID);
                servicePrincipalKey = parameterizablePasswordOrAKVtoSecretBase(linkedService, DynamicsAXLinkedService.SERVICE_PRINCIPLE_KEY, this.context);
            } else if (linkedService.authenticationType === dynamicsAxAuthTypes.UserAuth) {
                credential = linkedService.credential
                    ? ReferenceAdapter.toResource(linkedService.credential, this.context) as Model.CredentialReference
                    : undefined;
            }
        } else {
            servicePrincipalId = this.valueToResource(linkedService, DynamicsAXLinkedService.SERVICE_PRINCIPLE_ID);
            servicePrincipalKey = parameterizablePasswordOrAKVtoSecretBase(linkedService, DynamicsAXLinkedService.SERVICE_PRINCIPLE_KEY, this.context);
        }

        properties.typeProperties = {
            url: this.valueToResource(linkedService, DynamicsAXLinkedService.URL),
            tenant: this.valueToResource(linkedService, DynamicsAXLinkedService.TENANT),
            aadResourceId: this.valueToResource(linkedService, DynamicsAXLinkedService.AAD_RESOURCE_ID),
            servicePrincipalId,
            servicePrincipalKey: servicePrincipalKey as Model.SecretBase,
            credential
        };

        return linkedServiceResource;
    }

    protected async propertiesToModelAsync(linkedServiceResource: Model.LinkedServiceResource, linkedService: DynamicsAXLinkedService): Promise<DynamicsAXLinkedService> {
        const properties = linkedServiceResource.properties as Model.DynamicsAXLinkedService;
        const typeProperties: Model.DynamicsAXLinkedServiceTypeProperties = properties.typeProperties;

        ValueAdapter.toModel(linkedService, DynamicsAXLinkedService.URL, typeProperties.url);
        ValueAdapter.toModel(linkedService, DynamicsAXLinkedService.TENANT, typeProperties.tenant);
        ValueAdapter.toModel(linkedService, DynamicsAXLinkedService.AAD_RESOURCE_ID, typeProperties.aadResourceId);

        if (GlobalVariables.enableConnectorUserAuthCred) {
            if (typeProperties.servicePrincipalId) {
                linkedService.authenticationType = dynamicsAxAuthTypes.ServicePrincipal;
                ValueAdapter.toModel(linkedService, DynamicsAXLinkedService.SERVICE_PRINCIPLE_ID, typeProperties.servicePrincipalId);
                parameterizableSecretBaseToModel(linkedService, DynamicsAXLinkedService.SERVICE_PRINCIPLE_KEY, typeProperties.servicePrincipalKey, this.context, true);
            } else if (typeProperties.credential) {
                linkedService.authenticationType = dynamicsAxAuthTypes.UserAuth;
                linkedService.credential = ReferenceAdapter.toProxyModel(Metamodel.modelFor(Credential), typeProperties.credential, this.context);
            }
        } else {
            ValueAdapter.toModel(linkedService, DynamicsAXLinkedService.SERVICE_PRINCIPLE_ID, typeProperties.servicePrincipalId);
            parameterizableSecretBaseToModel(linkedService, DynamicsAXLinkedService.SERVICE_PRINCIPLE_KEY, typeProperties.servicePrincipalKey, this.context, true);
        }

        return linkedService;
    }
}
